/**
 * Opens a page's {@link PageInfoDialog} from any trigger. `PageInfoButton` is
 * the ⓘ beside a title; canvas pages with their own toolbar chrome (the 3D
 * viewer, Drawings) render their own trigger and use this directly.
 *
 * It also opens the dialog by itself when the reader arrives from another
 * page's search result ("Go to …"), with that task expanded.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";
import { PageInfoDialog, type PageInfoScope } from "./PageInfoDialog";
import { peekPageInfoOpen, takePageInfoOpen } from "./pendingPageInfoOpen";
import { PAGE_INFO } from "./pageInfoRegistry";

/** Lets the arriving page render the controls "Show me" may point at. */
const ARRIVAL_OPEN_DELAY_MS = 400;

export interface PageInfoOptions {
  /**
   * The project the page is scoped to. Defaults to the `?project=` URL param,
   * which `useBuildingScope` keeps in step with the picker in Projects view.
   */
  projectId?: number | null;
  /** Raise the dialog over a raised host (the fullscreen 3D viewer). */
  overlayClassName?: string;
  dialogClassName?: string;
}

function positiveInt(raw: string | null | undefined): number | null {
  const id = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Read at open time from the address bar, so no router hook is needed here. */
function scopeFromUrl(): PageInfoScope {
  const { pathname, search } = window.location;
  return {
    buildingId: positiveInt(/^\/buildings\/(\d+)/.exec(pathname)?.[1]),
    projectId:
      positiveInt(new URLSearchParams(search).get("project")) ??
      positiveInt(/^\/projects\/(\d+)/.exec(pathname)?.[1]),
  };
}

export function usePageInfoDialog(
  page: PageInfoId,
  { projectId, overlayClassName, dialogClassName }: PageInfoOptions = {}
): {
  label: string;
  openDialog: (initialItem?: string | null) => void;
  dialog: ReactNode;
  /** Attach to the trigger so closing returns focus to it. */
  triggerRef: RefObject<HTMLButtonElement>;
} {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [initialItem, setInitialItem] = useState<string | null>(null);
  const [scope, setScope] = useState<PageInfoScope>({ buildingId: null, projectId: null });
  const label = fill(t.pageInfo.buttonLabel, { page: PAGE_INFO[page].title(t) });

  const openDialog = (item: string | null = null) => {
    const fromUrl = scopeFromUrl();
    setInitialItem(item);
    setScope(
      projectId === undefined ? fromUrl : { ...fromUrl, projectId: positiveInt(String(projectId)) }
    );
    setOpen(true);
  };

  // Keyed on the page as well as mount: Settings and Organisation settings swap
  // the page their one title belongs to when the section changes.
  const openRef = useRef(openDialog);
  openRef.current = openDialog;
  useEffect(() => {
    if (!peekPageInfoOpen(page)) return;
    // Consumed only when it opens: a mount that is torn down first leaves it for the next.
    const timer = setTimeout(() => {
      const item = takePageInfoOpen(page);
      if (item) openRef.current(item);
    }, ARRIVAL_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [page]);

  const dialog = (
    <PageInfoDialog
      page={page}
      open={open}
      onOpenChange={setOpen}
      scope={scope}
      initialItem={initialItem}
      returnFocusRef={triggerRef}
      overlayClassName={overlayClassName}
      contentClassName={dialogClassName}
    />
  );

  return { label, openDialog, dialog, triggerRef };
}
